import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import { parseWaTemplate } from "../../utils/waTemplateParser";

// Single-student SMS / WhatsApp notify for the Attendance Command Center.
//
// WhatsApp: lists the institute's APPROVED c3crm.wa_template library (the same
// source as Communication Management → Templates — includes the attendance
// suite: day1_absent_alert, day2..6+ consecutive absence, student_absent_basic,
// student_attendance_warning, …). Send goes through the backend's
// POST /attendance/notify-student with the picked wa_template id + edited
// params, delivered via the C3CRM template-send using the client's own WABA.
//
// SMS: uses the DLT event templates (education_mngt.notification_template →
// sms_templates) via getExamEventTemplates, resolved server-side at send time.
const BACKEND = (
  process.env.REACT_APP_API_URL || "http://localhost:3008/graphql"
).replace(/\/graphql\/?$/, "");
const REST = `${BACKEND}/api/v1/attendance`;

// Variable definitions (notification_template) so a picked template's params fill from its
// OWN spec — contact_number, student_name, current_date, absent_days — not a positional guess.
const GET_TEMPLATE_DEFS = gql`
  query($institute_id: Int!, $channel: String!) {
    getNotificationTemplateDefs(institute_id: $institute_id, channel: $channel) {
      event_code
      provider_template_id
      template_variables
    }
  }
`;

const GET_WA_TEMPLATES = gql`
  query getWaTemplatesByInstitute($institute_id: Int) {
    getWaTemplatesByInstitute(institute_id: $institute_id) {
      id
      type
      template_name
      template_category
      template_lang
      template_form
      template_header
      image_size
      status
    }
  }
`;

const GET_EVENT_TEMPLATES = gql`
  query($employee_id: Int, $channel: String, $institute_id: Int) {
    getExamEventTemplates(employee_id: $employee_id, channel: $channel, institute_id: $institute_id) {
      event_code
      template_name
      language
      body
    }
  }
`;

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: "fa-whatsapp", desc: "Approved template to the parent" },
  { key: "sms", label: "SMS", icon: "fa-sms", desc: "DLT template to the parent" },
];

const ACCENT = "#0ea5e9";
// Preferred default templates for a single-day absent alert, in order.
const WA_DEFAULT_PREFERENCE = [/day1_absent/i, /absent_basic/i, /atten/i, /absent/i];

class NotifyStudentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      channel: props.channel || "whatsapp",
      // WhatsApp: approved wa_template library
      waTemplates: [], tvByPid: {}, waLoading: false, waTplId: null, waParams: [],
      // SMS: DLT event templates. smsContact fills {#cbn#} slots, smsDays
      // fills {#num#} (consecutive-absent days in the day6+ template).
      smsTemplates: [], smsLoading: false, smsEvent: "attendance_absent",
      smsContact: localStorage.getItem("rizee_cc_sms_contact") || "", smsDays: "6",
      sending: false, result: null, error: "",
    };
  }

  componentDidMount() {
    if (this.props.show) this.fetchForChannel();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.show && !prevProps.show) {
      this.setState(
        { channel: this.props.channel || "whatsapp", result: null, error: "", sending: false },
        this.fetchForChannel,
      );
      return;
    }
    if (this.props.show && this.state.channel !== prevState.channel) this.fetchForChannel();
  }

  instId = () => parseInt(Cookies.get("institutionid")) || 0;
  userId = () => parseInt(Cookies.get("user_id") || Cookies.get("id")) || 0;
  college = () => Cookies.get("institutionname") || "College";

  fetchForChannel = () => {
    if (this.state.channel === "whatsapp") this.fetchWaTemplates();
    else this.fetchSmsTemplates();
  };

  // Positional pre-fill for a template's {{n}} slots. Every field stays
  // editable, so a template with a different variable order is a quick fix.
  // Fill a picked template's params. If notification_template defines this wa_template
  // (tvByPid[templateId]), use its spec — static values (contact_number) verbatim, dynamic
  // keys resolved (student_name / current_date / absent_days). Otherwise fall back to the
  // positional guess (name, date, college, pct, 75%).
  defaultParams = (count, templateId) => {
    const s = this.props.student || {};
    const def = templateId != null ? (this.state.tvByPid || {})[String(templateId)] : null;
    if (Array.isArray(def) && def[0] && def[0].parameters) {
      const resolveKey = (k) => {
        switch (String(k || "")) {
          case "student_name": case "name": return s.student_name || `Adm ${s.admission_no}`;
          case "current_date": case "date": case "attendance_date": return this.dateLabel();
          case "absent_days": case "days": case "count": case "consecutive_days":
            return s.consec != null ? String(s.consec) : "";
          case "college_name": case "institute_name": return this.college();
          default: return "";
        }
      };
      return def[0].parameters.map((p) => (p.mode === "static" ? String(p.text != null ? p.text : "") : resolveKey(p.key || p.dynamic_key)));
    }
    const defaults = [
      s.student_name || `Adm ${s.admission_no}`,
      this.dateLabel(),
      this.college(),
      s.pct != null ? `${s.pct}%` : "",
      "75%",
    ];
    return Array.from({ length: count }, (_, i) => defaults[i] || "");
  };

  fetchWaTemplates = async () => {
    if (this.state.waTemplates.length) return; // library is static per session
    this.setState({ waLoading: true });
    try {
      const [res, defsRes] = await Promise.all([
        this.props.client.query({ query: GET_WA_TEMPLATES, variables: { institute_id: this.instId() }, fetchPolicy: "cache-first" }),
        this.props.client.query({ query: GET_TEMPLATE_DEFS, variables: { institute_id: this.instId(), channel: "whatsapp" }, fetchPolicy: "cache-first" }).catch(() => ({ data: {} })),
      ]);
      // Map provider_template_id -> parsed template_variables (the wa_template id matches it).
      const tvByPid = {};
      ((defsRes.data && defsRes.data.getNotificationTemplateDefs) || []).forEach((d) => {
        if (d.provider_template_id == null || !d.template_variables) return;
        try { tvByPid[String(d.provider_template_id)] = JSON.parse(d.template_variables); } catch (e) { /* skip */ }
      });
      const raw = (res.data && res.data.getWaTemplatesByInstitute) || [];
      const templates = raw
        .map((t) => {
          const parsed = parseWaTemplate(t);
          return {
            id: t.id,
            name: t.template_name || "Untitled",
            lang: t.template_lang || "en",
            status: t.status || "Pending",
            body: parsed.body || "",
            paramCount: parsed.paramCount || 0,
          };
        })
        .filter((t) => String(t.status).toLowerCase() === "approved");
      // Attendance-suite templates first, then the rest alphabetically.
      const rank = (t) => {
        const i = WA_DEFAULT_PREFERENCE.findIndex((rx) => rx.test(t.name));
        return i === -1 ? WA_DEFAULT_PREFERENCE.length : i;
      };
      templates.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
      const first = templates[0] || null;
      this.setState({
        waTemplates: templates,
        tvByPid,
        waLoading: false,
        waTplId: first ? first.id : null,
        waParams: first ? this.defaultParams(first.paramCount, first.id) : [],
      });
    } catch (e) {
      this.setState({ waTemplates: [], waLoading: false });
    }
  };

  fetchSmsTemplates = async () => {
    this.setState({ smsLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_EVENT_TEMPLATES,
        // institute_id (cookie) drives the notification_template -> sms_templates lookup;
        // it wins over the employee-derived institute so the SMS list can't come back empty.
        variables: { employee_id: this.userId(), channel: "sms", institute_id: this.instId() },
        fetchPolicy: "network-only",
      });
      const list = (res.data && res.data.getExamEventTemplates) || [];
      this.setState((s) => {
        const has = list.some((t) => t.event_code === s.smsEvent);
        const preferred = list.some((t) => t.event_code === "attendance_absent")
          ? "attendance_absent"
          : list[0] ? list[0].event_code : s.smsEvent;
        return { smsTemplates: list, smsLoading: false, smsEvent: has ? s.smsEvent : preferred };
      });
    } catch (e) {
      this.setState({ smsTemplates: [], smsLoading: false });
    }
  };

  selectWaTemplate = (id) => {
    const tpl = this.state.waTemplates.find((t) => String(t.id) === String(id));
    this.setState({ waTplId: id, waParams: tpl ? this.defaultParams(tpl.paramCount, id) : [] });
  };

  updateWaParam = (idx, value) =>
    this.setState((s) => ({ waParams: s.waParams.map((p, i) => (i === idx ? value : p)) }));

  recipient = () => {
    const s = this.props.student || {};
    return this.props.testNumber || s.father_phone || s.mother_phone || s.student_mobile || "";
  };

  dateLabel = () =>
    new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  fillSlots = (body, vals) => {
    let i = 0;
    return String(body || "")
      .replace(/\{\{(\d+)\}\}/g, (_, n) => {
        const v = vals[Number(n) - 1];
        return v != null && v !== "" ? v : `{{${n}}}`;
      })
      .replace(/\{#var#\}/g, () => (i < vals.length ? vals[i++] : "{#var#}"));
  };

  // DLT bodies use TYPED slots ({#alp#} text, {#num#} number, {#cbn#} contact);
  // fill each type from its own queue in order — mirrors the backend fill so the
  // preview matches what actually goes out. Unfilled slots stay visible.
  fillTyped = (body, { alp = [], num = [], cbn = [] }) => {
    const queues = { alp: [...alp], num: [...num], cbn: [...cbn] };
    return String(body || "").replace(/\{#(\w+)#\}/g, (m, typ) => {
      const q = queues[typ] || queues.alp;
      const v = q.length ? q.shift() : null;
      return v != null && v !== "" ? v : m;
    });
  };

  handleSend = async () => {
    const { student, testNumber } = this.props;
    const { channel, waTplId, waTemplates, waParams, smsEvent } = this.state;
    const to = this.recipient();
    if (!to) { this.setState({ error: "No phone number for this student." }); return; }
    const waTpl = channel === "whatsapp"
      ? waTemplates.find((t) => String(t.id) === String(waTplId))
      : null;
    if (channel === "whatsapp" && !waTpl) { this.setState({ error: "Pick a template first." }); return; }
    const ask = testNumber
      ? `🧪 TEST MODE — send this ${channel.toUpperCase()} to ${testNumber} (your test number)?`
      : `Send a REAL ${channel.toUpperCase()} to ${student.student_name}'s parent (${to})?`;
    if (!window.confirm(ask)) return;
    this.setState({ sending: true, error: "", result: null });
    try {
      const payload = {
        institute_id: this.instId(),
        channel,
        to,
        student_name: student.student_name || `Adm ${student.admission_no}`,
        date: this.dateLabel(),
        edu_student_id: student.student_id || null,
        att_date: this.props.month || null,
        institute_name: this.college(),
      };
      if (channel === "whatsapp") {
        payload.wa_template_id = waTpl.id;
        payload.template_name = waTpl.name;
        payload.variables = waParams.slice(0, waTpl.paramCount);
      } else {
        payload.event_code = smsEvent;
        payload.contact_number = this.state.smsContact || null;
        payload.absent_classes = parseInt(this.state.smsDays, 10) || 0;
      }
      const r = await fetch(`${REST}/notify-student`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || "Send failed");
      this.setState({ sending: false, result: { channel, to, template: data.template } });
    } catch (e) {
      this.setState({ sending: false, error: e.message });
    }
  };

  render() {
    const { show, onHide, student, testNumber } = this.props;
    const {
      channel, sending, result, error,
      waTemplates, waLoading, waTplId, waParams,
      smsTemplates, smsLoading, smsEvent,
    } = this.state;
    if (!student) return null;
    const to = this.recipient();
    const lbl = { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px" };
    const inp = { width: "100%", borderRadius: 8, border: "1.5px solid #e5e7eb", padding: "8px 10px", fontSize: 13 };
    const waTpl = waTemplates.find((t) => String(t.id) === String(waTplId));
    const smsTpl = smsTemplates.find((t) => t.event_code === smsEvent);
    const smsNeedsCbn = !!(smsTpl && /\{#cbn#\}/.test(smsTpl.body || ""));
    const smsNeedsNum = !!(smsTpl && /\{#num#\}/.test(smsTpl.body || ""));
    const preview = channel === "whatsapp"
      ? (waTpl ? this.fillSlots(waTpl.body, waParams) : "")
      : (smsTpl
          ? this.fillTyped(smsTpl.body, {
              alp: [student.student_name || "", this.dateLabel()],
              num: [this.state.smsDays],
              cbn: [this.state.smsContact],
            })
          : "");
    const canSend = channel === "whatsapp" ? !!waTpl : smsTemplates.length > 0;

    return (
      <Modal show={show} onHide={onHide} centered size="md">
        <Modal.Header closeButton style={{ background: ACCENT, color: "#fff", border: "none" }}>
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 600 }}>
            <i className="fas fa-bell mr-2" /> Notify Parent — {student.student_name}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2" style={{ fontSize: 13.5, color: "#475569" }}>
            <strong>{student.student_name}</strong> (Adm {student.admission_no})
            {student.pct != null ? <> · {student.pct}% attendance this month</> : null}
          </p>

          {testNumber ? (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, color: "#b45309", fontWeight: 700, marginBottom: 12 }}>
              🧪 TEST MODE — delivers to {testNumber}, not to the parent.
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: to ? "#475569" : "#dc2626", fontWeight: 600, marginBottom: 12 }}>
              Recipient (parent): {to || "no phone on record"}
            </div>
          )}

          {result ? (
            <div style={{ background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontWeight: 700, color: "#0e7490", marginBottom: 6 }}>
                <i className="fas fa-paper-plane mr-2" />Sent ({result.channel})
              </div>
              <div style={{ fontSize: 13, color: "#334155" }}>
                Delivered to <strong>{result.to}</strong> using template <strong>{result.template}</strong>.
              </div>
            </div>
          ) : (
            <>
              <label style={lbl}>Channel</label>
              <div style={{ display: "flex", flexDirection: "row", gap: 8, marginTop: 8 }}>
                {CHANNELS.map((c) => (
                  <div
                    key={c.key}
                    title={c.desc}
                    onClick={() => !sending && this.setState({ channel: c.key, error: "" })}
                    style={{
                      flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                      border: `1.5px solid ${channel === c.key ? ACCENT : "#e5e7eb"}`,
                      background: channel === c.key ? "#ecfeff" : "#fff",
                    }}
                  >
                    <i className={`${c.key === "whatsapp" ? "fab" : "fas"} ${c.icon}`} style={{ fontSize: 20, color: channel === c.key ? ACCENT : "#94a3b8" }} />
                    <div style={{ fontWeight: 600, fontSize: 12.5, color: channel === c.key ? "#0e7490" : "#1f2937" }}>{c.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 14 }}>
                <label style={lbl}>Template</label>

                {channel === "whatsapp" ? (
                  waLoading ? (
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                      <span className="spinner-border spinner-border-sm mr-2" />Loading approved templates…
                    </div>
                  ) : waTemplates.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: "#dc2626", marginTop: 6 }}>
                      No approved WhatsApp templates found for this institute's client.
                    </div>
                  ) : (
                    <>
                      <select
                        style={{ ...inp, marginTop: 6 }}
                        value={waTplId || ""}
                        disabled={sending}
                        onChange={(e) => this.selectWaTemplate(e.target.value)}
                      >
                        {waTemplates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} ({t.lang})</option>
                        ))}
                      </select>
                      {waTpl && waTpl.paramCount > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <label style={lbl}>Parameters</label>
                          {waParams.map((p, i) => (
                            <div key={i} style={{ marginBottom: 6 }}>
                              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>{`{{${i + 1}}}`}</span>
                              <input
                                style={inp}
                                value={p}
                                disabled={sending}
                                placeholder={`Value for {{${i + 1}}}`}
                                onChange={(e) => this.updateWaParam(i, e.target.value)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )
                ) : smsLoading ? (
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                    <span className="spinner-border spinner-border-sm mr-2" />Loading DLT templates…
                  </div>
                ) : smsTemplates.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: "#dc2626", marginTop: 6 }}>
                    No DLT SMS templates registered for this institute.
                    Add a <strong>notification_template</strong> row (event_code <strong>attendance_absent</strong>, channel sms).
                  </div>
                ) : (
                  <>
                    <select
                      style={{ ...inp, marginTop: 6 }}
                      value={smsEvent}
                      disabled={sending}
                      onChange={(e) => this.setState({ smsEvent: e.target.value })}
                    >
                      {smsTemplates.map((t) => (
                        <option key={t.event_code} value={t.event_code}>
                          {t.template_name || t.event_code} ({t.event_code})
                        </option>
                      ))}
                    </select>
                    {smsNeedsCbn && (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>College contact number ({"{#cbn#}"}) — leave empty to auto-use the branch's contact_no</span>
                        <input
                          style={inp}
                          value={this.state.smsContact}
                          disabled={sending}
                          placeholder="e.g. 9876543210"
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^\d+ ]/g, "");
                            localStorage.setItem("rizee_cc_sms_contact", v);
                            this.setState({ smsContact: v });
                          }}
                        />
                      </div>
                    )}
                    {smsNeedsNum && (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>Consecutive absent days ({"{#num#}"})</span>
                        <input
                          style={inp}
                          value={this.state.smsDays}
                          disabled={sending}
                          onChange={(e) => this.setState({ smsDays: e.target.value.replace(/\D/g, "") })}
                        />
                      </div>
                    )}
                  </>
                )}

                <label style={{ ...lbl, display: "block", marginTop: 10 }}>Preview</label>
                <div style={{ background: channel === "whatsapp" ? "#dcf8c6" : "#f1f5f9", borderRadius: 10, padding: "10px 12px", fontSize: 12.5, color: "#1e293b", whiteSpace: "pre-wrap", minHeight: 40 }}>
                  {preview || <span style={{ color: "#94a3b8" }}>No body preview available.</span>}
                </div>
                {channel === "sms" && (
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                    Sent exactly as the DLT-approved body; name/date/contact/days fill the typed slots server-side.
                    Unfilled slots stay visible above — fill them before sending.
                  </div>
                )}
              </div>

              {error ? <div className="alert alert-danger py-2 mt-3 mb-0" style={{ fontSize: 12.5 }}>{error}</div> : null}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {result ? (
            <Button variant="success" onClick={onHide}>Done</Button>
          ) : (
            <>
              <Button variant="light" onClick={onHide} disabled={sending}>Cancel</Button>
              <Button
                style={{ background: ACCENT, borderColor: ACCENT }}
                onClick={this.handleSend}
                disabled={sending || !to || !canSend}
              >
                {sending
                  ? <><span className="spinner-border spinner-border-sm mr-2" />Sending…</>
                  : <><i className="fas fa-paper-plane mr-1" /> Send{testNumber ? " (test)" : ""}</>}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withApollo(NotifyStudentModal);
